import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaFilter, FaSearch, FaEye } from "react-icons/fa";
import { FiAlertCircle } from "react-icons/fi";
import useReadAllCharterRequests from "../Libs/useReadAllCharterRequests";
import Skeleton from "react-loading-skeleton";
import "./CharterRequests.css";
import { calculateWorkflowStep } from "../../Utils/workflowStepCalculator";

export default function CharterRequests() {
  const [activeTab, setActiveTab] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  // API hooks
  const { charterRequests, charterRequestsLoading, charterRequestsError } = useReadAllCharterRequests();

  const getStatusClass = (status) => {
    switch(status) {
      case "pending": return "pending";
      case "approved": return "approved";
      case "in-use": return "in-use";
      case "declined": return "declined";
      case "Completed": return "approved";
      default: return "pending";
    }
  };

  // Map API step to display step (12-step workflow)
  const getDisplayStep = (apiStep) => {
    switch (apiStep) {
      case 1: return 1; // charterRequest (charterer)
      case 2: return 3; // charterResponse (vessel owner)
      case 3: return 4; // processCharterResponse (admin)
      case 4: return 5; // isCharterMovingForward (charterer)
      case 5: return 6; // processCharterMovingForward (admin)
      case 6: return 7; // selectMeetingDate (both)
      case 7: return 8; // meeting result saved (system)
      case 8: return 9; // signVesselNominationAgreement (both)
      case 9: return 10; // processVesselInspectionResult (admin)
      case 10: return 11; // signCharterPartyAgreement (both)
      case 11: return 12; // processCharterPartyAgreement (admin)
      default: return apiStep; // includes 12 -> 12
    }
  };

  const getComputedStatus = (request) => {
    const apiStep = request.step !== undefined ? request.step : calculateWorkflowStep(request);
    const displayStep = getDisplayStep(apiStep);
    if (displayStep >= 12) return "Completed";
    return request.status || "pending";
  };



  // Action handlers
  const handleViewDetails = (requestId) => {
    navigate(`/dashboard/charter-requests/${requestId}`);
  };

  // Filter charter requests based on active tab and search query
  const filteredRequests = charterRequests.filter(request => {
    const apiStep = request.step !== undefined ? request.step : calculateWorkflowStep(request);
    const displayStep = getDisplayStep(apiStep);

    // Filter by tab
    if (activeTab === "Accepted" && request.status !== "approved") {
      return false;
    }
    if (activeTab === "Declined" && request.status !== "declined") {
      return false;
    }
    if (activeTab === "Completed" && displayStep < 12) {
      return false;
    }
    
    // Filter by search query
    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase();
      return (
        request.scopeOfWork?.toLowerCase().includes(query) ||
        request.technicalRequirements?.toLowerCase().includes(query) ||
        request._id?.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const truncateText = (text, maxLength = 30) => {
    if (!text) return "N/A";
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  if (charterRequestsLoading) {
    return (
      <div className="charter-request-container">
        <div className="charter-request-tabs">
          <Skeleton height={40} width={80} style={{ marginRight: 16 }} />
          <Skeleton height={40} width={80} style={{ marginRight: 16 }} />
          <Skeleton height={40} width={80} />
        </div>
        <div className="charter-request-actions">
          <Skeleton height={40} width={100} style={{ marginRight: 16 }} />
          <Skeleton height={40} width={200} />
        </div>
        <div style={{ marginTop: 20 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} height={60} style={{ marginBottom: 12 }} />
          ))}
        </div>
      </div>
    );
  }

  if (charterRequestsError) {
    return (
      <div className="charter-request-container">
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <FiAlertCircle size={48} style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 18, fontWeight: 500 }}>Pending Approval</div>
          <div style={{ fontSize: 14, color: '#888', marginTop: 4 }}>Your charter requests are being reviewed.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="charter-request-container">
      {/* Tabs */}
      <div className="charter-request-tabs">
        <div 
          className={`charter-request-tab ${activeTab === "All" ? "active" : ""}`}
          onClick={() => setActiveTab("All")}
        >
          All ({charterRequests.length})
        </div>
        <div 
          className={`charter-request-tab ${activeTab === "Accepted" ? "active" : ""}`}
          onClick={() => setActiveTab("Accepted")}
        >
          Accepted ({charterRequests.filter(r => r.status === "approved").length})
        </div>
        <div 
          className={`charter-request-tab ${activeTab === "Declined" ? "active" : ""}`}
          onClick={() => setActiveTab("Declined")}
        >
          Declined ({charterRequests.filter(r => r.status === "declined").length})
        </div>
        <div 
          className={`charter-request-tab ${activeTab === "Completed" ? "active" : ""}`}
          onClick={() => setActiveTab("Completed")}
        >
          Completed ({charterRequests.filter(r => getDisplayStep(r.step !== undefined ? r.step : calculateWorkflowStep(r)) >= 12).length})
        </div>
      </div>
      
      {/* Actions */}
      <div className="charter-request-actions">
        <div className="charter-request-sort">
          <FaFilter />
          <span>Sort</span>
        </div>
        
        <div className="charter-request-search">
          <input 
            type="text" 
            placeholder="Search request" 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <FaSearch />
        </div>
      </div>
      
      {/* Table */}
      <table className="charter-request-table">
        <thead>
          <tr>
            <th className="charter-request-table-header">Request ID</th>
            <th className="charter-request-table-header">Start Date</th>
            <th className="charter-request-table-header">Duration</th>
            <th className="charter-request-table-header">Pricing</th>
            <th className="charter-request-table-header">Scope of Work</th>
            <th className="charter-request-table-header">Status</th>
            <th className="charter-request-table-header">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredRequests.length > 0 ? (
            filteredRequests.map(request => (
              <tr key={request._id} className="charter-request-table-row">
                <td className="charter-request-table-cell">
                  <div className="charter-request-id">
                    <span className="request-id-text">#{request._id.slice(-8)}</span>
                  </div>
                </td>
                <td className="charter-request-table-cell">
                  {request.startDate ? formatDate(request.startDate) : "N/A"}
                </td>
                <td className="charter-request-table-cell">
                  <span className="duration-badge">
                    {request.duration} days
                  </span>
                </td>
                <td className="charter-request-table-cell">
                  <span className="pricing-text">
                    {request.pricing ? formatPrice(request.pricing) : "N/A"}
                  </span>
                </td>
                <td className="charter-request-table-cell">
                  <div className="scope-work-cell">
                    <span className="scope-text">
                      {truncateText(request.scopeOfWork)}
                    </span>
                    {request.numberOfPassengers && (
                      <span className="passengers-badge">
                        {request.numberOfPassengers} passengers
                      </span>
                    )}
                  </div>
                </td>
                <td className="charter-request-table-cell">
                  <span className={`charter-request-status ${getStatusClass(getComputedStatus(request))}`}>
                    {getComputedStatus(request)}
                  </span>
                </td>
                <td className="charter-request-table-cell">
                  <button 
                    className="view-details-btn"
                    onClick={() => handleViewDetails(request._id)}
                  >
                    <FaEye /> View Details
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="7" style={{ textAlign: "center", padding: "2rem" }}>
                No charter requests found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
} 